import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FaCog, FaExclamationTriangle, FaFacebookMessenger, FaInstagram } from "react-icons/fa";
import { useNotify } from "../../components/ConfirmModal";
import { Avatar, Empty, PageHead } from "./WorkUi";
import { errorMessage, fmtShortDate } from "./workApi";
import { fmtTime, useClients } from "./WhatsAppPage";
import "../dm/dm.css";
import "./work.css";

const CHANNEL_ICONS = { Facebook: FaFacebookMessenger, Instagram: FaInstagram };
const FILTERS = [
  { key: "", label: "All" },
  { key: "Facebook", label: "Messenger" },
  { key: "Instagram", label: "Instagram" },
];

const displayName = (t) => t.Name || (t.Username ? `@${t.Username}` : `${t.Channel} user`);

function Thread({ api, threadId, onChanged }) {
  const notify = useNotify();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.socialThread(threadId));
    } catch (err) {
      notify.error(errorMessage(err, "Could not load the conversation"));
    }
  }, [api, threadId, notify]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.replySocialMessage(threadId, text.trim());
      setText("");
      await load();
      onChanged();
    } catch (err) {
      notify.error(errorMessage(err, "Could not send"));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="wk-muted">Loading…</div>;
  const t = data.thread;
  const Icon = CHANNEL_ICONS[t.Platform] || FaFacebookMessenger;
  return (
    <div className="wk-wa-thread">
      <div className="wk-wa-thread__head">
        <Avatar name={displayName(t)} size={34} />
        <div style={{ flex: 1 }}>
          <strong>{displayName(t)}</strong>
          <div className="wk-muted" style={{ fontSize: 12 }}>
            <Icon aria-hidden="true" /> {t.Channel} · {t.Account_Name}
            {t.Username && t.Name ? ` · @${t.Username}` : ""}
            {t.Website_Lead_Id ? (
              <>
                {" · "}
                <Link to={api.link("website-leads")}>Lead #{t.Website_Lead_Id}</Link>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <ol className="wk-wa-msgs" aria-label="Messages">
        {data.messages.map((m) => (
          <li key={m.Message_Id} className={`wk-wa-msg wk-wa-msg--${m.Direction}`}>
            {m.Referral?.ad_id ? <div className="wk-wa-msg__ref">From ad {m.Referral.ad_id}</div> : null}
            <div style={{ whiteSpace: "pre-wrap" }}>{m.Body || `[${m.Msg_Type}]`}</div>
            {m.Attachments.filter((a) => a.url).map((a, i) => (
              <div key={i}>
                <a href={a.url} target="_blank" rel="noreferrer">
                  Open {a.type || "attachment"}
                </a>
              </div>
            ))}
            <div className="wk-wa-msg__meta">
              {fmtTime(m.Created_On)}
              {m.Status === "failed" ? (
                <span title={m.Error || "Failed"}>
                  {" "}
                  <FaExclamationTriangle aria-label="failed" />
                </span>
              ) : null}
            </div>
            {m.Error ? <div className="wk-wa-msg__err">{m.Error}</div> : null}
          </li>
        ))}
      </ol>
      <form onSubmit={send} className="wk-wa-compose">
        {t.Window_Open ? (
          <>
            <label className="wk-sr-only" htmlFor={`dm-reply-${threadId}`}>
              Reply
            </label>
            <textarea
              id={`dm-reply-${threadId}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write a reply…"
              maxLength={t.Platform === "Instagram" ? 1000 : 2000}
              rows={2}
            />
            <button type="submit" className="dmp-btn" disabled={busy || !text.trim()}>
              Send
            </button>
          </>
        ) : (
          <p className="wk-muted" style={{ margin: 0 }}>
            More than 24 hours since they last wrote, so Meta does not allow a reply from here. Answer from Meta Business
            Suite if needed; you can reply here again as soon as they write.
          </p>
        )}
      </form>
    </div>
  );
}

/** DMS > Messages: Messenger and Instagram Direct conversations of the client's connected accounts. */
function MessagesPage({ api }) {
  const [params, setParams] = useSearchParams();
  const clients = useClients(api);
  const tenantId = params.get("tenant") || "";
  const platform = params.get("platform") || "";
  const threadId = Number(params.get("thread")) || null;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [unread, setUnread] = useState(false);

  const setQuery = (next) => {
    const merged = {
      ...(tenantId ? { tenant: tenantId } : {}),
      ...(platform ? { platform } : {}),
      ...(threadId ? { thread: threadId } : {}),
      ...next,
    };
    Object.keys(merged).forEach((k) => (merged[k] === null || merged[k] === "") && delete merged[k]);
    setParams(merged, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      setData(
        await api.socialThreads({
          tenant_id: api.isStaff ? tenantId || undefined : undefined,
          platform: platform || undefined,
          search: search || undefined,
          unread: unread ? 1 : undefined,
        })
      );
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load conversations"));
    }
  }, [api, tenantId, platform, search, unread]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="dmp wk">
      <PageHead title="Messages">
        {api.isStaff ? (
          <select
            value={tenantId}
            onChange={(e) => setQuery({ tenant: e.target.value, thread: null })}
            aria-label="Client"
            style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: "7px 10px", minHeight: 38 }}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        ) : null}
        {api.isStaff ? (
          <Link className="dmp-btn dmp-btn--ghost" to={api.link("work/connections")}>
            <FaCog aria-hidden="true" /> Pages &amp; setup
          </Link>
        ) : null}
      </PageHead>
      <div className="dmp-card">
        <div className="wk-wa">
          <div className="wk-wa-list">
            <div className="wk-cal-toolbar" style={{ flexWrap: "wrap" }}>
              <label className="wk-sr-only" htmlFor="dm-search">
                Search conversations
              </label>
              <input
                id="dm-search"
                className="wk-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or username"
                style={{ flex: 1 }}
              />
              <button type="button" className="wk-chip" aria-pressed={unread} onClick={() => setUnread((v) => !v)}>
                Unread{data?.unread ? ` (${data.unread})` : ""}
              </button>
            </div>
            <div className="wk-cal-toolbar" role="group" aria-label="Channel">
              {FILTERS.map((f) => (
                <button
                  key={f.key || "all"}
                  type="button"
                  className="wk-chip"
                  aria-pressed={platform === f.key}
                  onClick={() => setQuery({ platform: f.key, thread: null })}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {error ? <div className="dmp-error">{error}</div> : null}
            {!data ? (
              <div className="wk-muted">Loading…</div>
            ) : data.conversations.length ? (
              <ul className="wk-wa-convos">
                {data.conversations.map((c) => {
                  const Icon = CHANNEL_ICONS[c.Platform] || FaFacebookMessenger;
                  return (
                    <li key={c.Thread_Id}>
                      <button
                        type="button"
                        className={`wk-wa-convo${c.Thread_Id === threadId ? " is-active" : ""}`}
                        onClick={() => setQuery({ thread: c.Thread_Id })}
                        aria-current={c.Thread_Id === threadId ? "true" : undefined}
                      >
                        <Avatar name={displayName(c)} size={30} />
                        <span className="wk-wa-convo__body">
                          <span className="wk-wa-convo__name">
                            <span>
                              <Icon aria-label={c.Channel} /> {displayName(c)}
                            </span>
                            <span className="wk-muted">{c.Last_Message_At ? fmtShortDate(c.Last_Message_At) : ""}</span>
                          </span>
                          <span className="wk-wa-convo__last">
                            {c.Last_Direction === "out" ? "You: " : ""}
                            {c.Last_Message}
                          </span>
                        </span>
                        {c.Unread ? (
                          <span className="wk-wa-badge" aria-label={`${c.Unread} unread`}>
                            {c.Unread}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <Empty icon={FaFacebookMessenger} title="No messages yet">
                When people message the client's Facebook page or Instagram account (or tap a click-to-message ad),
                conversations appear here and new people become leads.
              </Empty>
            )}
          </div>
          <div className="wk-wa-pane">
            {threadId ? (
              <Thread key={threadId} api={api} threadId={threadId} onChanged={load} />
            ) : (
              <Empty icon={FaFacebookMessenger} title="Pick a conversation to read and reply" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MessagesPage;
